import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@groot/ui/select";
import { useAIModels } from "@/hooks/api/useAIModels";
import type { AIModelOption } from "@/types";

interface ModelSelectProps {
  value?: string;
  onChange: (modelId: string | undefined) => void;
  id?: string;
  label?: string;
  className?: string;
  hideLabel?: boolean;
}

/**
 * Dropdown for selecting an LLM model for statement extraction.
 *
 * Models are fetched from `/api/v1/ai/models` and grouped into
 * "Recommended" and "Other" sections.
 */
export function ModelSelect({
  value,
  onChange,
  id = "ai-model",
  label = "AI Model",
  className,
  hideLabel,
}: ModelSelectProps) {
  const { data, isLoading } = useAIModels();

  const models = data?.models ?? [];
  const defaultModel = data?.defaultModel;

  // Selected value for the dropdown. Falls back to the server default so
  // the trigger always shows a meaningful label.
  const selected = value || defaultModel || "";

  const recommended = models.filter((m) => m.recommended);
  const others = models.filter((m) => !m.recommended);

  const renderItems = (items: AIModelOption[]) =>
    items.map((m) => (
      <SelectItem key={m.id} value={m.id}>
        {m.label}
        {!m.nativeDocuments && <span className="text-muted-foreground ml-1 text-xs">(image)</span>}
      </SelectItem>
    ));

  return (
    <div className={className}>
      {!hideLabel && (
        <Label htmlFor={id} className="text-sm">
          {label}
        </Label>
      )}
      {isLoading ? (
        <div className="flex items-center h-9 mt-1">
          <LoadingSpinner className="size-4" />
        </div>
      ) : (
        <Select
          value={selected}
          onValueChange={(v) => onChange(v === selected && !value ? undefined : v)}
        >
          <SelectTrigger id={id} className="mt-1">
            <SelectValue placeholder="Default model" />
          </SelectTrigger>
          <SelectContent>
            {recommended.length > 0 && (
              <SelectGroup>
                <SelectLabel>Recommended</SelectLabel>
                {renderItems(recommended)}
              </SelectGroup>
            )}
            {others.length > 0 && (
              <SelectGroup>
                <SelectLabel>Other</SelectLabel>
                {renderItems(others)}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      )}
      <p className="text-xs text-muted-foreground mt-1">
        {value
          ? "Overrides the default extraction model for this run."
          : "Defaults to the recommended model if unset."}
      </p>
    </div>
  );
}
